export type TradeSelectionUpdater = (selected: ReadonlySet<string>) => Set<string>;

/**
 * Snapshot one blotter click before React queues the state update.
 *
 * Both the anchor and visible row order are captured here so a deferred state
 * updater cannot observe a newer anchor or a refetched/reordered result page.
 */
export function createTradeSelectionUpdater(
  rowIds: readonly string[],
  clickedId: string,
  anchorId: string | null,
  shiftKey: boolean
): TradeSelectionUpdater {
  const visibleIds = [...rowIds];
  const clickedIndex = visibleIds.indexOf(clickedId);
  const anchorIndex = shiftKey && anchorId != null ? visibleIds.indexOf(anchorId) : -1;
  const rangeIds =
    clickedIndex >= 0 && anchorIndex >= 0
      ? visibleIds.slice(
          Math.min(anchorIndex, clickedIndex),
          Math.max(anchorIndex, clickedIndex) + 1
        )
      : null;

  return (selected) => {
    const next = new Set(selected);
    if (clickedIndex < 0) return next;

    if (rangeIds) {
      // The clicked row decides whether the inclusive range is added or removed.
      const add = !next.has(clickedId);
      for (const id of rangeIds) {
        if (add) next.add(id);
        else next.delete(id);
      }
    } else if (next.has(clickedId)) {
      next.delete(clickedId);
    } else {
      next.add(clickedId);
    }

    return next;
  };
}
